import DeleteActionIcon from "@/assets/icons/DeleteAction.svg?react";
import EditActionIcon from "@/assets/icons/EditAction.svg?react";
import CheckCircleIcon from "@/assets/icons/Iconex/Light/Check Circle 1.svg?react";
import CloseIcon from "@/assets/icons/Iconex/Light/Close2.svg?react";
import ReloginIcon from "@/assets/icons/Iconex/Light/Synchronize.svg?react";
import PauseActionIcon from "@/assets/icons/PauseAction.svg?react";
import { useTranslation } from "@/hooks";
import {
	Box,
	Button,
	Dialog,
	SvgIcon,
	Typography,
	styled,
} from "@mui/material";
import type { ReactNode } from "react";

type Variant =
	| "edit"
	| "delete"
	| "pause"
	| "play"
	| "cancel"
	| "accept"
	| "reject"
	| "relogin";

interface ActionDialogProps {
	variant: Variant;
	text?: ReactNode;
	loading?: boolean;
	open: boolean;
	onClose: () => void;
	onConfirm: () => void;
}

interface ActionConfig {
	icon: React.ReactElement;
	defaultTitle: string;
	defaultCta: string;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		backgroundColor: theme.palette.background.paper,
		paddingInline: theme.spacing(10),
		paddingBlock: theme.spacing(7),
		borderRadius: theme.spacing(6),
	},
}));

const ActionDialog: React.FC<ActionDialogProps> = ({
	variant = "delete",
	loading = false,
	text,
	open,
	onClose,
	onConfirm,
}) => {
	const { t } = useTranslation();

	const CONFIG: Record<Variant, ActionConfig> = {
		edit: {
			icon: <EditActionIcon />,
			defaultTitle: t("action.areYouSure"),
			defaultCta: t("action.edit"),
		},
		pause: {
			icon: <PauseActionIcon />,
			defaultTitle: t("action.areYouSure"),
			defaultCta: t("action.button.pause"),
		},
		delete: {
			icon: <DeleteActionIcon />,
			defaultTitle: t("action.areYouSure"),
			defaultCta: t("action.button.delete"),
		},
		play: {
			icon: <PauseActionIcon />,
			defaultTitle: t("action.areYouSure"),
			defaultCta: t("action.button.play"),
		},
		cancel: {
			icon: <DeleteActionIcon />,
			defaultTitle: t("action.areYouSure"),
			defaultCta: t("action.button.confirmCancel"),
		},
		accept: {
			icon: (
				<SvgIcon sx={{ color: "success.main" }}>
					<CheckCircleIcon />
				</SvgIcon>
			),
			defaultTitle: t("action.areYouSure"),
			defaultCta: t("action.button.confirmAccept"),
		},
		reject: {
			icon: <DeleteActionIcon />,
			defaultTitle: t("action.areYouSure"),
			defaultCta: t("action.button.confirmReject"),
		},
		relogin: {
			icon: (
				<SvgIcon sx={{ color: "warning.main" }}>
					<ReloginIcon />
				</SvgIcon>
			),
			defaultTitle: t("action.areYouSure"),
			defaultCta: t("action.button.confirmRelogin"),
		},
	};
	return (
		<StyledDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
			<Box textAlign="end">
				<SvgIcon onClick={onClose} sx={{ cursor: "pointer" }}>
					<CloseIcon />
				</SvgIcon>
			</Box>

			<Box display="flex" alignItems="center" gap={3} sx={{ mb: 3 }}>
				{CONFIG[variant].icon}
				<Typography variant="h5">{CONFIG[variant].defaultTitle}</Typography>
			</Box>

			<Typography variant="body1" sx={{ color: "grey.300", mb: 9 }}>
				{text}
			</Typography>

			<Box display="flex" gap={3}>
				<Button
					fullWidth
					size="large"
					variant="outlined"
					onClick={onClose}
					sx={{ textTransform: "none" }}
				>
					{t("common.button.no")}
				</Button>
				<Button fullWidth size="large" loading={loading} onClick={onConfirm}>
					{CONFIG[variant].defaultCta}
				</Button>
			</Box>
		</StyledDialog>
	);
};

export default ActionDialog;
